import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MobileUserModal = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return parseFloat(num).toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 3 
    });
  };

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.85)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'modalFadeIn 0.3s ease-out',
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(180deg, #002200 0%, #001100 50%, #000800 100%)',
          border: '2px solid #00ff41',
          borderRadius: '8px',
          padding: '20px',
          width: '100%',
          maxWidth: '350px',
          boxShadow: '0 0 30px rgba(64, 255, 65, 0.4)',
          position: 'relative',
          animation: 'modalSlideIn 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #00ff41',
          paddingBottom: '12px',
        }}>
          <h3 style={{
            color: '#40ff60',
            fontFamily: "'VT323', monospace",
            fontSize: '1.3rem',
            margin: 0,
            textShadow: '0 0 5px #00ff41',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}>
            User Profile
          </h3>
          
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #00ff41',
              color: '#00ff41',
              fontSize: '1.2rem',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#00ff41';
              e.target.style.color = '#000800';
              e.target.style.boxShadow = '0 0 8px #00ff41';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#00ff41';
              e.target.style.boxShadow = 'none';
            }}
          >
            ✕
          </button>
        </div>

        {/* User Stats */}
        <div style={{
          display: 'grid',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <div style={{
            background: 'rgba(64, 255, 64, 0.1)',
            border: '1px solid #00ff41',
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '0.9rem',
              color: '#C0FFC0',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontFamily: "'VT323', monospace",
            }}>
              Username
            </div>
            <div style={{
              fontSize: '1.1rem',
              color: '#40ff60',
              fontWeight: 'bold',
              textShadow: '0 0 3px #00ff41',
              fontFamily: "'VT323', monospace",
            }}>
              {user?.username || 'Unknown'}
            </div>
          </div>

          <div style={{
            background: 'rgba(64, 255, 64, 0.1)',
            border: '1px solid #00ff41',
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '0.9rem',
              color: '#C0FFC0',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontFamily: "'VT323', monospace",
            }}>
              WR Coins
            </div>
            <div style={{
              fontSize: '1.1rem',
              color: '#40ff60',
              fontWeight: 'bold',
              textShadow: '0 0 3px #00ff41',
              fontFamily: "'VT323', monospace",
            }}>
              💰 {formatNumber(user?.wrCoins)}
            </div>
          </div>

          <div style={{
            background: 'rgba(64, 255, 64, 0.1)',
            border: '1px solid #00ff41',
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '0.9rem',
              color: '#C0FFC0',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontFamily: "'VT323', monospace",
            }}>
              Experience
            </div>
            <div style={{
              fontSize: '1.1rem',
              color: '#40ff60',
              fontWeight: 'bold',
              textShadow: '0 0 3px #00ff41',
              fontFamily: "'VT323', monospace",
            }}>
              ⭐ {formatNumber(user?.xp)} XP
            </div>
          </div>

          {user?.role === 'admin' && (
            <div style={{
              background: 'rgba(255, 215, 0, 0.1)',
              border: '1px solid #ffd700',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '0.9rem',
                color: '#ffd700',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontFamily: "'VT323', monospace",
              }}>
                Role
              </div>
              <div style={{
                fontSize: '1.1rem',
                color: '#ffd700',
                fontWeight: 'bold',
                textShadow: '0 0 3px #ffd700',
                fontFamily: "'VT323', monospace",
              }}>
                👑 Administrator
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <button
            onClick={() => handleNavigation('/profile')}
            style={{
              background: 'linear-gradient(45deg, #001100, #002200)',
              color: '#C0FFC0',
              border: '2px solid #00ff41',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: "'VT323', monospace",
              fontSize: '1rem',
              textDecoration: 'none',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minHeight: '44px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #002200, #00ff41)';
              e.target.style.color = '#000800';
              e.target.style.boxShadow = '0 0 12px rgba(64, 255, 64, 0.4)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #001100, #002200)';
              e.target.style.color = '#C0FFC0';
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
            onTouchStart={(e) => {
              e.target.style.transform = 'scale(0.98)';
            }}
            onTouchEnd={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <span>👤</span>
            Edit Profile
          </button>

          {user?.role === 'admin' && (
            <button
              onClick={() => handleNavigation('/admin')}
              style={{
                background: 'linear-gradient(45deg, #221100, #332200)',
                color: '#ffd700',
                border: '2px solid #ffd700',
                borderRadius: '8px',
                padding: '12px',
                fontFamily: "'VT323', monospace",
                fontSize: '1rem',
                textDecoration: 'none',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '44px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #332200, #ffd700)';
                e.target.style.color = '#000800';
                e.target.style.boxShadow = '0 0 12px rgba(255, 215, 0, 0.4)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #221100, #332200)';
                e.target.style.color = '#ffd700';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateY(0)';
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.98)';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <span>👑</span>
              Admin Panel
            </button>
          )}

          <button
            onClick={() => handleNavigation('/chat')}
            style={{
              background: 'linear-gradient(45deg, #001122, #002244)',
              color: '#40ff60',
              border: '2px solid #40ff60',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: "'VT323', monospace",
              fontSize: '1rem',
              textDecoration: 'none',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minHeight: '44px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #002244, #40ff60)';
              e.target.style.color = '#000800';
              e.target.style.boxShadow = '0 0 12px rgba(64, 255, 96, 0.4)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #001122, #002244)';
              e.target.style.color = '#40ff60';
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
            onTouchStart={(e) => {
              e.target.style.transform = 'scale(0.98)';
            }}
            onTouchEnd={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <span>🐰</span>
            White Rabbit
          </button>

          <button
            onClick={handleLogout}
            style={{
              background: 'linear-gradient(45deg, #221100, #331100)',
              color: '#ff4444',
              border: '2px solid #ff4444',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: "'VT323', monospace",
              fontSize: '1rem',
              textDecoration: 'none',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minHeight: '44px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginTop: '12px',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #331100, #ff4444)';
              e.target.style.color = 'white';
              e.target.style.boxShadow = '0 0 12px rgba(255, 68, 68, 0.4)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #221100, #331100)';
              e.target.style.color = '#ff4444';
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
            onTouchStart={(e) => {
              e.target.style.transform = 'scale(0.98)';
            }}
            onTouchEnd={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <span>🚪</span>
            Logout
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}
      </style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MobileUserModal;